import { APIClient } from "@heidelbergmaterials/fcl-apiclient"
import {
	getLegalDocument,
	getVersions,
	LegalDocumentType,
	LegalVersion,
} from "@shared/api"
import { User, updateUserById } from "@shared/api/authentication"
import DOMPurify from "dompurify"

interface LegalFlowParams {
	user?: User
	legalClient: APIClient
	authClient: APIClient
}

interface LegalDocumentVersion {
	docType: LegalDocumentType
	version?: LegalVersion
}

export class LegalFlow {
	user?: User
	legalClient: APIClient
	authClient: APIClient

	constructor({ user, legalClient, authClient }: LegalFlowParams) {
		this.user = user
		this.legalClient = legalClient
		this.authClient = authClient
	}

	private extractBodyContent(htmlContent: string): string {
		try {
			if (typeof DOMParser !== "undefined") {
				const parser = new DOMParser()
				const doc = parser.parseFromString(htmlContent, "text/html")
				const body = doc.querySelector("body")
				return body ? body.innerHTML : htmlContent
			}

			const bodyRegex = /<body[^>]*>([\s\S]*?)<\/body>/i
			const bodyMatch = bodyRegex.exec(htmlContent)
			return bodyMatch ? bodyMatch[1] : htmlContent
		} catch {
			return htmlContent
		}
	}

	async fetchActualLegalVersions(
		product: string,
		documentTypes: LegalDocumentType[],
		region: string,
	): Promise<LegalDocumentVersion[]> {
		const versions = await getVersions(this.legalClient)
		const requiredVersions: LegalDocumentVersion[] = []

		documentTypes.forEach(docType => {
			const productVersions = versions[product]
			const docTypeVersions = productVersions?.[docType]
			const regionVersions = docTypeVersions?.[region]

			if (!regionVersions || regionVersions.length === 0) {
				return
			}

			const latestVersion = [...regionVersions].sort(
				(a: LegalVersion, b: LegalVersion) =>
					Number(b.version) - Number(a.version),
			)[0]

			requiredVersions.push({
				docType,
				version: latestVersion,
			})
		})

		return requiredVersions
	}

	hasAcceptedLatestTerms(actualTCVersion: LegalVersion): boolean {
		return (
			!!actualTCVersion &&
			this.user?.termsAcceptedVersion === actualTCVersion.version
		)
	}

	hasAcceptedLatestTermsForDpp(actualDPPVersion: LegalVersion): boolean {
		return (
			!!actualDPPVersion &&
			this.user?.dataPrivacyAcknowledgedVersion ===
				actualDPPVersion.version
		)
	}

	async loadLegalDocuments(
		product: string,
		documentType: LegalDocumentType,
		region: string,
		languages: string[],
		version: string,
	): Promise<
		{
			documentType: LegalDocumentType
			language: string
			version: string
			content: string
		}[]
	> {
		const documentRequests: Array<{
			product: string
			documentType: LegalDocumentType
			region: string
			language: string
			version: string
		}> = []

		for (const language of languages) {
			documentRequests.push({
				product,
				documentType,
				region,
				language,
				version,
			})
		}

		const results = await Promise.all(
			documentRequests.map(async params => {
				const content = await getLegalDocument(this.legalClient, params)
				const bodyContent = this.extractBodyContent(
					DOMPurify.sanitize(content),
				)

				return {
					documentType: params.documentType,
					language: params.language,
					version: params.version,
					content: bodyContent,
				}
			}),
		)

		return results
	}

	async acceptTerms(version: string): Promise<void> {
		if (!this.user) {
			return
		}

		await updateUserById(this.authClient, this.user.id, {
			termsAcceptedVersion: version,
			termsAcceptedOn: new Date().toISOString(),
		})
	}

	async acceptDpp(version: string): Promise<void> {
		if (!this.user) {
			return
		}

		await updateUserById(this.authClient, this.user.id, {
			dataPrivacyAcknowledgedVersion: version,
			dataPrivacyAcknowledgedOn: new Date().toISOString(),
		})
	}
}
